import { createAppCacheReducer } from '@fullstory/redux-util/utils/appCache/reducers';
import { createRequestReducer } from '@fullstory/redux-util';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { UserDetailsResponse } from '@fullstory/client-api';
import { SearchieActionTypes, SearchieRequestTypes } from './types';
import { SearchieActions } from './actions';
import {
  GenerateSearchieResponseProto,
  SearchieDailyActivesFrequency,
  SearchieDailyActivesSettings,
  SearchieMobileDevicesPivot,
  SearchieMobileDevicesSettings,
  SearchieTimeRangeCountsSettings,
  SearchieTopBadClicksGroupBy,
  SearchieTopBadClicksPivot,
  SearchieTopBadClicksSettings,
} from '@fullstory/proto/fs/services/alexa';

const data = createAppCacheReducer<GenerateSearchieResponseProto>()(
  SearchieRequestTypes.SearchieAppCacheIsLoading,
  SearchieRequestTypes.SearchieAppCacheLoadFailed,
  SearchieRequestTypes.SearchieAppCacheRefetching,
  SearchieRequestTypes.SearchieAppCacheHasData,
  SearchieRequestTypes.SearchieAppCacheFresh,
  SearchieRequestTypes.SearchieAppCacheSwap,
  SearchieRequestTypes.SearchieAppCacheClear,
)();

const defaultTopBadClickState: SearchieTopBadClicksSettings = {
  pivot: SearchieTopBadClicksPivot.SEARCHIE_TOP_BAD_CLICKS_PIVOT_EVENT,
  limit: 0,
  groupBy: SearchieTopBadClicksGroupBy.SEARCHIE_TOP_BAD_CLICKS_GROUP_BY_TARGET_TEXT,
};

type SettingsState = {
  userTrends: SearchieDailyActivesSettings;
  rageClicks: SearchieTopBadClicksSettings;
  errorClicks: SearchieTopBadClicksSettings;
  deadClicks: SearchieTopBadClicksSettings;
  mobileDevices: SearchieMobileDevicesSettings;
  timeRange: SearchieTimeRangeCountsSettings;
};

const settings = combineReducers<SettingsState>({
  userTrends: (
    state: SearchieDailyActivesSettings = {
      frequencies: [SearchieDailyActivesFrequency.SEARCHIE_DAILY_ACTIVES_FREQUENCY_DAILY],
      computePast: false,
      computeRatio: false,
      alignWeekdays: true,
    },
    action: SearchieActions,
  ) => {
    switch (action.type) {
      case SearchieActionTypes.UserTrendsSettingsChange:
        return action.settings;
    }
    return state;
  },
  rageClicks: (
    state: SearchieTopBadClicksSettings = { ...defaultTopBadClickState },
    action: SearchieActions,
  ) => {
    switch (action.type) {
      case SearchieActionTypes.RageClicksSettingsChange:
        return action.settings;
    }
    return state;
  },
  errorClicks: (
    state: SearchieTopBadClicksSettings = { ...defaultTopBadClickState },
    action: SearchieActions,
  ) => {
    switch (action.type) {
      case SearchieActionTypes.ErrorClicksSettingsChange:
        return action.settings;
    }
    return state;
  },
  deadClicks: (
    state: SearchieTopBadClicksSettings = { ...defaultTopBadClickState },
    action: SearchieActions,
  ) => {
    switch (action.type) {
      case SearchieActionTypes.DeadClicksSettingsChange:
        return action.settings;
    }
    return state;
  },
  mobileDevices: (
    state: SearchieMobileDevicesSettings = {
      pivot: SearchieMobileDevicesPivot.SEARCHIE_MOBILE_DEVICES_PIVOT_MODEL,
    },
    action: SearchieActions,
  ) => {
    switch (action.type) {
      case SearchieActionTypes.MobileDeviceSettingsChange:
        return action.settings;
    }
    return state;
  },
  timeRange: (state: SearchieTimeRangeCountsSettings = { countMatching: true }, _: any) => state,
});

const oneMostActiveUsers = createRequestReducer<UserDetailsResponse>()(
  SearchieRequestTypes.UserDetailsRequest,
  SearchieRequestTypes.UserDetailsRequestSuccess,
  SearchieRequestTypes.UserDetailsRequestFailure,
);

type SearchieReducerState = {
  data: ReturnType<typeof data>;
  settings: ReturnType<typeof settings>;
  oneMostActiveUsers: ReturnType<typeof oneMostActiveUsers>;
};

export const searchieReducer = combineReducers<SearchieReducerState>({
  data,
  settings,
  oneMostActiveUsers,
});
